// DS9.13 Post Compact Card
import React from 'react';
import cn from 'classnames';

import { PapiDeviation } from '@wix/da-papi-types';
import StarIcon from '@wix/da-ds/pkg/Icons/24x24/FavStarFill';
import CommentIcon from '@wix/da-ds/pkg/Icons/24x24/Comment';
import UserAvatar from '../User/Avatar';
import UserLink from '../User/Link/UserLink';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';

import { DeviationTypes } from '../types/deviation';
import { getDeviationType } from '../DeviationViews/_helpers';
import Dumbo from '../DeviationViews/Dumbo';
import Timestamp from '../Timestamp';
import ExcerptRenderer from '../TextRenderer/ExcerptRenderer';

import s from './PostCompactCard.scss';

export interface Props {
  deviation: PapiDeviation;
  showUsername?: boolean;
  showBackground?: boolean;
  className?: string;
}

const PostCompactCard: React.FC<Props> = ({
  deviation,
  showUsername = false,
  showBackground = true,
  className,
}) => {
  const deviationType = getDeviationType(deviation);
  const isJournal = deviationType === DeviationTypes.JOURNAL;
  const isStatusUpdate = deviationType === DeviationTypes.STATUS;

  const faves = deviation.stats?.favourites || 0;
  const comments = deviation.stats?.comments || 0;

  const hasBackgroundImage = showBackground && !!deviation.coverImage;

  return (
    <Section
      className={cn(
        s['root'],
        isJournal && s['journal'],
        isStatusUpdate && s['status-update'],
        hasBackgroundImage && s['with-background'],
        className
      )}
    >
      {hasBackgroundImage && (
        <div className={s['background']}>
          <Dumbo
            className={s['background-image']}
            deviation={deviation.coverImage || deviation}
            width={400}
            useTextlessMode
          />
        </div>
      )}

      <div className={s['content']}>
        <div className={s['main-content']}>
          <Heading className={s['title']}>{deviation.title}</Heading>

          <div className={s['excerpt']}>
            {deviation.textContent && (
              <ExcerptRenderer
                textContent={deviation.textContent}
                htmlExcerptLegacy
              />
            )}
          </div>

          <div className={s['meta']}>
            <Timestamp timestamp={deviation.publishedTime} />
            <span className={s['type']}>
              {isJournal ? 'Journal' : 'Status Update'}
            </span>
          </div>
        </div>

        <div className={s['footer']}>
          {showUsername && deviation.author && (
            <div className={s['user-info']}>
              <UserAvatar
                className={s['avatar']}
                user={deviation.author}
                withTooltip={false}
                withLink={true}
                size={24}
              />
              <UserLink
                className={s['username']}
                user={deviation.author}
                showSymbol={false}
              />
            </div>
          )}

          <div className={s['stats']}>
            <div className={s['stat']}>
              <StarIcon className={s['stat-icon']} />
              <span className={s['stat-count']}>{faves}</span>
            </div>
            <div className={s['stat']}>
              <CommentIcon className={s['stat-icon']} />
              <span className={s['stat-count']}>{comments}</span>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

PostCompactCard.displayName = 'PostCompactCard';

export default PostCompactCard;
